import { styled } from '@mui/material/styles';
import { Box, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { VenueType, VenueTypeCode } from '../constants/venueTypes';
import { venueTypeColors } from '../../../theme/venueTypeColors';
import { useLandingAnalytics } from '../hooks/useLandingAnalytics';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  GamingScene,
  SnookerScene,
  KidsScene,
} from '../../../components/ui/illustrations/venue-themed';

interface VenueTypeCardProps {
  venueType: VenueTypeCode;
  title: string;
  description: string;
  icon: React.ReactNode;
  venueCount?: number;
  onClick: () => void;
}

const CardContainer = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<{ bgColor: string }>(({ theme, bgColor }) => ({
  position: 'relative',
  background: `linear-gradient(135deg, ${bgColor}12 0%, ${bgColor}06 100%)`,
  border: `2px solid ${bgColor}40`,
  borderRadius: 24,
  padding: theme.spacing(4),
  cursor: 'pointer',
  overflow: 'hidden',
  transition: 'all 0.25s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 24,
    background: `linear-gradient(135deg, transparent 0%, ${bgColor}10 100%)`,
    opacity: 0,
    transition: 'opacity 0.3s',
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 32px ${bgColor}20`,
    borderColor: bgColor,
    '&::before': {
      opacity: 1,
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<{ bgColor: string }>(({ bgColor }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: `${bgColor}20`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 24,
  fontSize: '2.5rem',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 700,
  fontSize: '1.75rem',
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const Description = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '1rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(3),
  lineHeight: 1.6,
}));

const VenueCount = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ theme, color }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 600,
  fontSize: '2rem',
  color: color,
  marginBottom: 8,
}));

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'venueColor',
})<{ venueColor: string }>(({ venueColor, theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: 12,
  padding: '12px 24px',
  backgroundColor: venueColor,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: venueColor,
    opacity: 0.9,
    transform: 'scale(1.05)',
  },
}));

export function VenueTypeCard({
  venueType,
  title,
  description,
  icon,
  venueCount,
  onClick,
}: VenueTypeCardProps) {
  const colors = venueTypeColors[venueType];
  const { track } = useLandingAnalytics();

  const handleClick = () => {
    track({ action: 'venue_card_click', venueType });
    onClick();
  };

  const borderRadiusMap: Record<string, string> = {
    [VenueType.GAMING]: '8px',
    [VenueType.SNOOKER]: '16px',
    [VenueType.KIDS_PLAYAREA]: '20px',
  };

  const themedBorderRadius = borderRadiusMap[venueType]
    ? { borderRadius: borderRadiusMap[venueType] }
    : undefined;

  return (
    <CardContainer bgColor={colors.primary} onClick={handleClick} style={themedBorderRadius}>
      {venueType === VenueType.GAMING && <GamingScene />}
      {venueType === VenueType.SNOOKER && <SnookerScene />}
      {venueType === VenueType.KIDS_PLAYAREA && <KidsScene />}

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <IconWrapper bgColor={colors.primary}>{icon}</IconWrapper>

        <Title>{title}</Title>
        <Description>{description}</Description>

        {venueCount !== undefined && venueCount > 0 && (
          <VenueCount color={colors.primary}>
            {venueCount < 5 ? 'Handpicked Venues' : `${venueCount}+ Venues`}
          </VenueCount>
        )}
        {(venueCount === undefined || venueCount === 0) && (
          <VenueCount color={colors.primary} sx={{ fontSize: '1.25rem', opacity: 0.7 }}>
            Coming Soon
          </VenueCount>
        )}

        <StyledButton venueColor={colors.primary} endIcon={<ArrowForwardIcon />} fullWidth>
          {venueCount && venueCount > 0 ? `Explore ${title}` : 'Browse'}
        </StyledButton>
      </Box>
    </CardContainer>
  );
}
